import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { toast } from "./ui/sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { useApp } from "../context/AppContext";

const inp = "mt-1 w-full text-sm rounded-lg border border-slate-200 px-2.5 py-1.5";
const ALL_METHODS = ["Check 支票", "Wire 电汇", "Zelle", "Cash 现金", "Credit Card 信用卡", "Other 其他"];

/** 收款登记由用户确认后提交；在请求完成前锁定关闭入口，避免已到账却误以为取消。 */
export const PaymentRecordDialog = ({ zh, stageLabel, defaultAmount, description, confirmText, proofs = [], onConfirm, onClose }) => {
  const { cashMode } = useApp();
  const cashOff = cashMode && cashMode.enabled === false;
  const METHODS = cashOff ? ALL_METHODS.filter((m) => !/cash/i.test(m)) : ALL_METHODS;
  const [selectedProofId, setSelectedProofId] = useState(proofs[0]?.pid || "");
  const [f, setF] = useState({
    date: new Date().toISOString().slice(0, 10),
    amount: String(proofs[0]?.amount ?? defaultAmount ?? ""),
    method: "Check 支票",
    reference: "",
    note: "",
  });
  const [busy, setBusy] = useState(false);
  const selectedProof = proofs.find((p) => p.pid === selectedProofId);
  const changeProof = (pid) => {
    setSelectedProofId(pid);
    const proof = proofs.find((p) => p.pid === pid);
    setF((prev) => ({ ...prev, amount: String(proof?.amount ?? defaultAmount ?? "") }));
  };
  const save = async () => {
    if (busy) return;
    if (!f.date || !f.amount || !Number.isFinite(Number(f.amount)) || Number(f.amount) <= 0) { toast.error(zh ? "请填写收款日期与金额" : "Date & amount required"); return; }
    setBusy(true);
    try {
      await onConfirm({ date: f.date, amount: Number(f.amount), method: f.method, reference: f.reference.trim(), note: f.note.trim(), ...(selectedProof ? { proofId: selectedProof.pid } : {}) });
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(open) => { if (!open && !busy) onClose(); }}>
      <DialogContent className="max-w-sm" closeDisabled={busy} data-testid="payment-record-dialog" onEscapeKeyDown={(e) => { if (busy) e.preventDefault(); }} onInteractOutside={(e) => { if (busy) e.preventDefault(); }}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-600" />{zh ? "登记收款记录" : "Record Payment"}</DialogTitle>
          <DialogDescription>{description || (zh ? `「${stageLabel}」· 完整付款记录将写入对账单 Statement` : `${stageLabel} · recorded on the statement`)}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {proofs.length > 0 && <label className="block col-span-2"><span className="text-xs text-slate-500">{zh ? "待核对凭证 *" : "Proof to confirm *"}</span>
            <select data-testid="payrec-proof-id" className={inp} value={selectedProofId} disabled={busy} onChange={(e) => changeProof(e.target.value)}>
              {proofs.map((proof, index) => {
                const amount = proof.amount != null && Number.isFinite(Number(proof.amount)) ? `$${Number(proof.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : (zh ? "金额待填" : "amount needed");
                return <option key={proof.pid} value={proof.pid}>{proof.name || `${zh ? "凭证" : "Proof"} ${index + 1}`} · {proof.payerName || (zh ? "付款人未填" : "Payer unknown")} · {amount} · {proof.at ? String(proof.at).slice(0, 10) : "—"}</option>;
              })}
            </select></label>}
          <label className="block"><span className="text-xs text-slate-500">{zh ? "收款日期 *" : "Date *"}</span>
            <input data-testid="payrec-date" type="date" className={inp} value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></label>
          <label className="block"><span className="text-xs text-slate-500">{zh ? "实收金额 * ($)" : "Amount *"}</span>
            <input data-testid="payrec-amount" type="number" className={inp} value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} /></label>
          <label className="block"><span className="text-xs text-slate-500">{zh ? "收款方式" : "Method"}</span>
            <select data-testid="payrec-method" data-cash-off={cashOff ? "1" : "0"} className={inp} value={f.method} onChange={(e) => setF({ ...f, method: e.target.value })}>
              {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            {cashOff && <span data-testid="payrec-cash-off-hint" className="block text-[10px] text-amber-700 mt-0.5">{zh ? "现金功能已由管理员关闭，不可登记现金收款" : "Cash disabled by admin"}</span>}</label>
          <label className="block"><span className="text-xs text-slate-500">{zh ? "支票号/流水号" : "Check / Ref #"}</span>
            <input data-testid="payrec-reference" className={inp} value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} placeholder="#1937" /></label>
          <label className="block col-span-2"><span className="text-xs text-slate-500">{zh ? "备注" : "Note"}</span>
            <input data-testid="payrec-note" className={inp} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></label>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy} data-testid="payrec-cancel">{zh ? "取消" : "Cancel"}</Button>
          <Button size="sm" data-testid="payrec-save" onClick={save} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}{confirmText || (zh ? "确认收款" : "Confirm")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
